const normalizeWhitespace = (value) => String(value || '').trim().replace(/\s+/g, ' ')

export const DELIVERY_PROFILE_REQUIRED_FIELDS = [
  'recipient_name',
  'recipient_phone',
  'line1',
  'city',
  'county',
  'postal_code'
]

const DELIVERY_PROFILE_FIELD_LABELS = {
  recipient_name: 'numele destinatarului',
  recipient_phone: 'telefonul destinatarului',
  line1: 'adresa de livrare',
  city: 'orașul',
  county: 'județul',
  postal_code: 'codul poștal'
}

const hasMeaningfulDeliveryProfileData = (profile = {}) => [
  profile.label,
  profile.recipient_name,
  profile.recipient_phone,
  profile.company,
  profile.line1,
  profile.line2,
  profile.city,
  profile.county,
  profile.postal_code
].some(Boolean)

export const buildDeliveryAddressSummary = (profile = {}) => (
  [
    profile.line1,
    profile.line2,
    profile.city,
    profile.county,
    profile.postal_code,
    profile.country
  ].filter(Boolean).join(', ')
)

export const getMissingRequiredDeliveryFields = (profile = {}) => (
  DELIVERY_PROFILE_REQUIRED_FIELDS.filter(field => !normalizeWhitespace(profile[field]))
)

export const buildDeliveryProfileValidationMessage = (missingFields = []) => {
  if (!missingFields.length) {
    return ''
  }

  const labels = missingFields.map(field => DELIVERY_PROFILE_FIELD_LABELS[field] || field)
  if (labels.length === 1) {
    return `Completează ${labels[0]}.`
  }
  return `Completează câmpurile obligatorii: ${labels.join(', ')}.`
}

const normalizeDeliveryProfile = (profile = {}, fallbackId = '') => {
  const normalized = {
    id: normalizeWhitespace(profile.id || fallbackId),
    label: normalizeWhitespace(profile.label),
    recipient_name: normalizeWhitespace(profile.recipient_name),
    recipient_phone: normalizeWhitespace(profile.recipient_phone),
    company: normalizeWhitespace(profile.company),
    line1: normalizeWhitespace(profile.line1),
    line2: normalizeWhitespace(profile.line2),
    city: normalizeWhitespace(profile.city),
    county: normalizeWhitespace(profile.county),
    postal_code: normalizeWhitespace(profile.postal_code),
    country: normalizeWhitespace(profile.country || 'RO')
  }

  if (!normalized.id || !hasMeaningfulDeliveryProfileData(normalized)) {
    return null
  }

  normalized.summary = buildDeliveryAddressSummary(normalized)
  normalized.display_name = normalized.label || normalized.recipient_name || normalized.city || 'Adresă salvată'
  normalized.missing_required_fields = getMissingRequiredDeliveryFields(normalized)
  normalized.is_valid = normalized.missing_required_fields.length === 0
  return normalized
}

export const normalizeManagedDeliveryProfiles = (userData = {}) => {
  const explicitProfiles = Array.isArray(userData.delivery_profiles) ? userData.delivery_profiles : []
  const nextProfiles = []
  const seen = new Set()

  explicitProfiles.forEach((profile, index) => {
    if (!profile || profile.same_as_billing === true) {
      return
    }

    const normalized = normalizeDeliveryProfile(profile, `delivery-${index + 1}`)
    if (!normalized || seen.has(normalized.id)) {
      return
    }
    nextProfiles.push(normalized)
    seen.add(normalized.id)
  })

  if (nextProfiles.length > 0) {
    return nextProfiles
  }

  const legacyProfile = userData.delivery_profile || {}
  if (legacyProfile.same_as_billing === true) {
    return []
  }

  const normalizedLegacy = normalizeDeliveryProfile(
    legacyProfile,
    normalizeWhitespace(userData.default_delivery_profile_id || legacyProfile.id || 'delivery-legacy')
  )

  if (!normalizedLegacy) {
    return []
  }
  return [normalizedLegacy]
}

export const normalizeSavedDeliveryProfiles = (userData = {}) => (
  normalizeManagedDeliveryProfiles(userData).filter(profile => profile.is_valid)
)

export const getDefaultDeliveryProfileId = (userData = {}, deliveryProfiles = []) => {
  const explicitDefault = normalizeWhitespace(userData.default_delivery_profile_id)
  if (explicitDefault && deliveryProfiles.some(profile => profile.id === explicitDefault)) {
    return explicitDefault
  }
  return deliveryProfiles[0]?.id || ''
}

export const getOrderedDeliveryProfiles = (deliveryProfiles = [], selectedId = '') => {
  if (!selectedId) {
    return deliveryProfiles
  }

  const selected = deliveryProfiles.find(profile => profile.id === selectedId)
  if (!selected) {
    return deliveryProfiles
  }

  return [
    selected,
    ...deliveryProfiles.filter(profile => profile.id !== selectedId)
  ]
}

export const buildDeliveryProfilePayload = (form = {}, fallbackId = '') => ({
  id: normalizeWhitespace(form.id || fallbackId),
  label: normalizeWhitespace(form.label),
  recipient_name: normalizeWhitespace(form.recipient_name),
  recipient_phone: normalizeWhitespace(form.recipient_phone),
  company: normalizeWhitespace(form.company),
  line1: normalizeWhitespace(form.line1),
  line2: normalizeWhitespace(form.line2),
  city: normalizeWhitespace(form.city),
  county: normalizeWhitespace(form.county),
  postal_code: normalizeWhitespace(form.postal_code),
  country: normalizeWhitespace(form.country || 'RO')
})

export const createEmptyDeliveryProfileForm = (defaults = {}) => ({
  id: defaults.id || '',
  label: defaults.label || '',
  recipient_name: defaults.recipient_name || defaults.username || '',
  recipient_phone: defaults.recipient_phone || defaults.phone || '',
  company: defaults.company || '',
  line1: defaults.line1 || '',
  line2: defaults.line2 || '',
  city: defaults.city || '',
  county: defaults.county || '',
  postal_code: defaults.postal_code || '',
  country: defaults.country || 'RO'
})
